import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ClaimState,
  Quiz,
  Question,
  LeadFormData,
  ClaimSubmissionStep,
  QuizFlowState,
} from "../../types";
import { PersonalizedOverviewResponse, QuizSignals } from "../../api/controllers/claim.controller";

const initialLeadFormData = {
  fullName: "",
  email: "",
  phone: "",
  street: "",
  city: "",
  state: "",
  zipCode: "",
  signature: null,
  lawsuitSubtype: null,
};

// Hydrate claims from localStorage for instant hard-refresh loads.
// Fresh API data replaces this within seconds (stale-while-revalidate).
const CLAIMS_CACHE_KEY = 'fairday_claims_cache';
function getCachedClaims(): Record<string, unknown>[] {
  try {
    const raw = localStorage.getItem(CLAIMS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Hydrate personalized overview from localStorage
const PERSONALIZED_OVERVIEW_KEY = 'fairday_personalized_overview';
function getCachedPersonalizedOverview(): PersonalizedOverviewResponse | null {
  try {
    const raw = localStorage.getItem(PERSONALIZED_OVERVIEW_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Hydrate quiz signals from localStorage
const QUIZ_SIGNALS_KEY = 'fairday_quiz_signals';
function getCachedQuizSignals(): QuizSignals | null {
  try {
    const raw = localStorage.getItem(QUIZ_SIGNALS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const initialState: ClaimState & {
  personalizedOverview: PersonalizedOverviewResponse | null;
  personalizedOverviewLoading: boolean;
  personalizedOverviewError: string | null;
  quizSignals: QuizSignals | null;
} = {
  currentSegment: "general",
  currentLandingTemplate: "classic",
  navHidden: false,
  showReviewInputs: false,
  quizResult: [],
  claims: getCachedClaims(),
  currentFlow: "quiz-landing",
  questions: { quizFlow: [], claimSubmissionFlow: [], additionalQuestions: [] },
  leadFormStep: "details",
  leadFormData: initialLeadFormData,
  leads: [],
  submittedQuizResult: [],
  quizCurrentStep: 1,
  quizScreenNumber: 0,
  // Personalized overview state
  personalizedOverview: getCachedPersonalizedOverview(),
  personalizedOverviewLoading: false,
  personalizedOverviewError: null,
  quizSignals: getCachedQuizSignals(),
};

const claimSlice = createSlice({
  name: "claim",
  initialState,
  reducers: {
    setCurrentSegment: (state, action: PayloadAction<string>) => {
      state.currentSegment = action.payload;
    },
    resetQuiz: (state) => {
      state.quizResult = [];
    },
    saveQuiz: (
      state,
      action: PayloadAction<{
        segment: string;
        sessionId?: string;
        quiz?: Quiz | Quiz[];
        estimatedCost?: number;
        isCompleted?: boolean;
      }>
    ) => {
      const { segment, sessionId, quiz, estimatedCost, isCompleted } =
        action.payload;

      const existingSegment = state.quizResult.find(
        (item) => item.segment === segment
      );

      if (!existingSegment) {
        state.quizResult.push({
          segment,
          sessionId: sessionId ?? null,
          estimatedCost: estimatedCost ?? 0,
          quiz: Array.isArray(quiz) ? quiz : quiz ? [quiz] : [],
          isCompleted: isCompleted || false,
        });
        return;
      }

      if (sessionId) existingSegment.sessionId = sessionId;
      if (isCompleted !== undefined) existingSegment.isCompleted = isCompleted;
      if (estimatedCost !== undefined) {
        existingSegment.estimatedCost = estimatedCost;
      }

      if (!quiz) return;

      // Callers reaching this branch pass a single Quiz (not an array).
      const singleQuiz = quiz as Quiz;
      const existingQuiz = existingSegment.quiz.find(
        (q) => q.questionId === singleQuiz.questionId
      );

      if (existingQuiz) {
        existingQuiz.answer = singleQuiz.answer;
      } else {
        existingSegment.quiz.push(singleQuiz);
      }
    },
    formatQuiz: (
      state,
      action: PayloadAction<{
        segment: string;
      }>
    ) => {
      const { segment } = action.payload;
      const segmentExists = state.quizResult.some(
        (item) => item.segment === segment
      );

      if (segmentExists) {
        state.quizResult = state.quizResult.map((item) =>
          item.segment === segment
            ? {
              ...item,
              quiz: [],
              sessionId: null,
              isCompleted: false,
            }
            : item
        );
      }
    },
    setClaims: (state, action: PayloadAction<Record<string, unknown>[]>) => {
      state.claims = action.payload;
      // Cache claims to localStorage for instant loads on hard refresh.
      // Best-effort — don't block the reducer if storage is full.
      try {
        localStorage.setItem(CLAIMS_CACHE_KEY, JSON.stringify(action.payload));
      } catch { /* silent — storage full or unavailable */ }
    },
    setClaimsLoading: (state, action: PayloadAction<boolean>) => {
      state.claimsLoading = action.payload;
    },
    setClaimsError: (state, action: PayloadAction<string | null>) => {
      state.claimsError = action.payload;
    },
    setQuestions: (
      state,
      action: PayloadAction<{
        quizFlow: Question[];
        claimSubmissionFlow: Question[];
        additionalQuestions?: Question[];
      }>
    ) => {
      state.questions = action.payload;
    },
    setLeadFormData: (state, action: PayloadAction<Partial<LeadFormData>>) => {
      state.leadFormData = {
        ...state.leadFormData,
        ...action.payload,
      };
    },
    setLeadFormStep: (state, action: PayloadAction<ClaimSubmissionStep>) => {
      state.leadFormStep = action.payload;
    },
    resetLeadForm: (state) => {
      state.leadFormData = initialLeadFormData;
      state.leadFormStep = "details";
    },
    setLeads: (state, action: PayloadAction<Record<string, unknown>[]>) => {
      state.leads = action.payload;
    },
    setCurrentFlow: (state, action: PayloadAction<QuizFlowState>) => {
      state.currentFlow = action.payload;
    },
    setSubmittedQuizResult: (state, action: PayloadAction<Record<string, unknown>[]>) => {
      state.submittedQuizResult = action.payload;
    },
    setSubmitPhoneNumber: (state, action: PayloadAction<string | undefined>) => {
      state.submitPhoneNumber = action.payload;
    },
    setCurrentLandingTemplate: (state, action: PayloadAction<string | undefined>) => {
      state.currentLandingTemplate = action.payload ?? "classic";
    },
    setNavHidden: (state, action: PayloadAction<boolean | undefined>) => {
      state.navHidden = action.payload ?? false;
    },
    setShowReviewInputs: (state, action: PayloadAction<boolean | undefined>) => {
      state.showReviewInputs = action.payload ?? false;
    },
    setQuizCurrentStep: (state, action: PayloadAction<number>) => {
      state.quizCurrentStep = action.payload;
    },
    setQuizScreenNumber: (state, action: PayloadAction<number>) => {
      state.quizScreenNumber = action.payload;
    },
    // Personalized overview reducers
    setPersonalizedOverview: (state, action: PayloadAction<PersonalizedOverviewResponse | null>) => {
      state.personalizedOverview = action.payload;
      // Cache to localStorage
      try {
        if (action.payload) {
          localStorage.setItem(PERSONALIZED_OVERVIEW_KEY, JSON.stringify(action.payload));
        } else {
          localStorage.removeItem(PERSONALIZED_OVERVIEW_KEY);
        }
      } catch { /* silent */ }
    },
    setPersonalizedOverviewLoading: (state, action: PayloadAction<boolean>) => {
      state.personalizedOverviewLoading = action.payload;
    },
    setPersonalizedOverviewError: (state, action: PayloadAction<string | null>) => {
      state.personalizedOverviewError = action.payload;
    },
    setQuizSignals: (state, action: PayloadAction<QuizSignals | null>) => {
      state.quizSignals = action.payload;
      // Cache to localStorage
      try {
        if (action.payload) {
          localStorage.setItem(QUIZ_SIGNALS_KEY, JSON.stringify(action.payload));
        } else {
          localStorage.removeItem(QUIZ_SIGNALS_KEY);
        }
      } catch { /* silent */ }
    },
  },
});

export const {
  setCurrentSegment,
  saveQuiz,
  resetQuiz,
  formatQuiz,
  setClaims,
  setClaimsLoading,
  setClaimsError,
  setQuestions,
  setLeadFormData,
  setLeadFormStep,
  resetLeadForm,
  setLeads,
  setCurrentFlow,
  setSubmittedQuizResult,
  setSubmitPhoneNumber,
  setCurrentLandingTemplate,
  setNavHidden,
  setShowReviewInputs,
  setQuizCurrentStep,
  setQuizScreenNumber,
  setPersonalizedOverview,
  setPersonalizedOverviewLoading,
  setPersonalizedOverviewError,
  setQuizSignals,
} = claimSlice.actions;

export default claimSlice.reducer;
